'use client';

import { useCallback, useState } from 'react';
import { useWizardContext } from '../../hooks/useWizardContext';
import { useWizardNavigation } from '../../hooks/useWizardNavigation';
import { fetchLocationByZipCode } from '../../services/LocationService';
import { StepLayout } from '../shared/StepLayout';
import { TextInput } from '../shared/TextInput';

/**
 * Validates a US ZIP code (5 digits, or 5+4 format).
 */
function isValidZipCode(zip: string): boolean {
  return /^\d{5}(-\d{4})?$/.test(zip);
}

/**
 * ZipCode step - collects the user's residential ZIP code.
 */
export function ZipCodeStep() {
  const { state, dispatch } = useWizardContext();
  const { goToNextStep, goToPreviousStep, hasPreviousStep } =
    useWizardNavigation();

  // Initialize from wizard state (session is already loaded at this point)
  const [zipCode, setZipCode] = useState(state.formData?.zip_code || '');
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setZipCode(e.target.value);
      if (error) {
        setError(undefined);
      }
    },
    [error]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const trimmedZip = zipCode.trim();

      if (!trimmedZip) {
        setError('ZIP code is required');
        return;
      }

      if (!isValidZipCode(trimmedZip)) {
        setError('Please enter a valid US ZIP code');
        return;
      }

      setIsLoading(true);
      setError(undefined);

      try {
        const locationData = await fetchLocationByZipCode(trimmedZip);

        // Location found - save it
        if (locationData) {
          dispatch({
            type: 'SET_FORM_DATA',
            payload: {
              zip_code: trimmedZip,
              location_has_pediatrics: locationData.hasPediatrics,
              nearest_location: {
                locationName: locationData.locationName,
                marketName: locationData.marketName,
                salesforceName: locationData.salesforceName,
                alternativeLocations: locationData.alternativeLocations,
              },
            },
          });
        } else {
          // No location found (404) - proceed without location data
          dispatch({
            type: 'SET_FORM_DATA',
            payload: {
              zip_code: trimmedZip,
              location_has_pediatrics: false,
              nearest_location: undefined,
            },
          });
        }

        goToNextStep();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to look up location. Please try again.'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [zipCode, dispatch, goToNextStep]
  );

  return (
    <StepLayout
      heading='Where do you live?'
      description='Please enter your primary ZIP code to find your nearest Sollis center.'
      onSubmit={handleSubmit}
      onBack={goToPreviousStep}
      showBack={hasPreviousStep}
      isSubmitting={isLoading}
    >
      <div>
        <TextInput
          type='text'
          id='zip_code'
          name='zip_code'
          value={zipCode}
          onChange={handleChange}
          error={error}
          placeholder='ZIP code'
          maxLength={10}
          inputMode='numeric'
          disabled={isLoading}
        />
        {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
      </div>
    </StepLayout>
  );
}
